import { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useStoreUrlParams } from "@/features/onboarding/hooks";
import { useEverflowClick } from "@/features/onboarding/hooks/use-everflow-click";
import { useLanguageFromParams } from "@/shared/hooks";
import { useMouseflowSession } from "@/shared/lib/mouseflow/hooks/use-mouseflow-session";
import { FunnelFooter } from "./funnel-footer";
import { FunnelHeader } from "./funnel-header";

/**
 * Root layout for the levelsRx-style funnel. Initializes the same tracking/session
 * hooks as the questionnaire layout, then renders the custom header, the active
 * step (via <Outlet />) and the legal footer inside one scroll container.
 */
export const FunnelLayout = () => {
  useLanguageFromParams();
  useEverflowClick();
  useStoreUrlParams();
  useMouseflowSession();

  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // Reset the scroll position to the top whenever the active step changes.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  // `html`'s global `overflow: hidden` only blocks user-driven scrolling, not the
  // browser's own scroll-into-view when a long step's hidden (sr-only) checkbox
  // input receives focus — that still shifts `html`'s scroll offset, pushing this
  // h-dvh layout past the viewport and exposing `html`'s background beneath the
  // footer. Locking `html` to the viewport keeps `main` as the only element that
  // can ever scroll.
  useEffect(() => {
    const { style: htmlStyle } = document.documentElement;
    const previousPosition = htmlStyle.position;
    const previousInset = htmlStyle.inset;

    htmlStyle.position = "fixed";
    htmlStyle.inset = "0";

    return () => {
      htmlStyle.position = previousPosition;
      htmlStyle.inset = previousInset;
    };
  }, []);

  return (
    <div className="flex h-dvh flex-col bg-white">
      <FunnelHeader />
      <main ref={mainRef} className="min-h-0 grow overflow-y-auto">
        <Outlet />
        <FunnelFooter />
      </main>
    </div>
  );
};
